/**
 * Admin Settings Component
 * 
 * Allows organization admins to create and manage users within their organization.
 * Features:
 * - Create new users with email, password, name, role
 * - View all organization users
 * - Edit user details and status
 * - Delete users
 * - Toggle active/inactive status
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { UserSession } from '../../types';
import {
  TeamMember,
  getTeamMembers,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
  toggleTeamMemberStatus,
  getTeamStats,
} from '../../services/teamService';
import './AdminSettings.css';

interface AdminSettingsProps {
  userSession: UserSession;
}

/**
 * Admin Settings page for user management
 */
const AdminSettings: React.FC<AdminSettingsProps> = ({ userSession }) => {
  const [users, setUsers] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create User Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserTag, setNewUserTag] = useState<'admin' | 'developer' | 'designer' | 'compliance' | 'tester'>('developer');
  const [isCreating, setIsCreating] = useState(false);

  // Edit User Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<TeamMember | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Load all team members
   */
  const loadUsers = useCallback(async () => {
    if (!userSession.adminId) return;

    setIsLoading(true);
    setError(null);

    try {
      const fetchedUsers = await getTeamMembers(userSession.adminId);
      setUsers(fetchedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load team members. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [userSession.adminId]);

  /**
   * Load team statistics
   */
  const loadStats = useCallback(async () => {
    if (!userSession.adminId) return;

    try {
      const fetchedStats = await getTeamStats(userSession.adminId);
      setStats(fetchedStats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [userSession.adminId]);

  /**
   * Load users and statistics on mount
   */
  useEffect(() => {
    if (userSession.adminId) {
      loadUsers();
      loadStats();
    }
  }, [userSession.adminId, loadUsers, loadStats]);

  /**
   * Handle creating a new team member
   */
  const handleCreateUser = async () => {
    if (!newUserEmail.trim() || !newUserPassword.trim() || !newUserName.trim()) {
      setError('All fields are required');
      return;
    }

    if (!userSession.adminId) {
      setError('Admin ID is missing');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await createTeamMember(
        userSession.adminId,
        newUserEmail.trim(),
        newUserPassword,
        newUserName.trim(),
        newUserTag
      );

      setSuccess(`Team member ${newUserName} created successfully!`);
      setShowCreateModal(false);

      // Reset form
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserName('');
      setNewUserTag('developer');

      // Reload users and stats
      loadUsers();
      loadStats();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create team member. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Handle editing a team member
   */
  const handleEditUser = (user: TeamMember) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  /**
   * Save edited team member
   */
  const handleSaveEdit = async () => {
    if (!editingUser || !userSession.adminId) return;

    setIsSaving(true);
    setError(null);

    try {
      await updateTeamMember(
        userSession.adminId,
        editingUser.id,
        {
          name: editingUser.name,
          tag: editingUser.tag,
          status: editingUser.status,
        }
      );

      setSuccess(`Team member ${editingUser.name} updated successfully!`);
      setShowEditModal(false);
      setEditingUser(null);

      // Reload users and stats
      loadUsers();
      loadStats();
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update team member. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle deleting a team member
   */
  const handleDeleteUser = async (user: TeamMember) => {
    if (!window.confirm(`Are you sure you want to delete team member "${user.name}"?`)) {
      return;
    }

    if (!userSession.adminId) {
      setError('Admin ID is missing');
      return;
    }

    try {
      await deleteTeamMember(userSession.adminId, user.id);
      setSuccess(`Team member ${user.name} deleted successfully!`);

      // Reload users and stats
      loadUsers();
      loadStats();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete team member. Please try again.');
    }
  };

  /**
   * Handle toggling team member status
   */
  const handleToggleStatus = async (user: TeamMember) => {
    if (!userSession.adminId) {
      setError('Admin ID is missing');
      return;
    }

    try {
      await toggleTeamMemberStatus(userSession.adminId, user.id, user.status);
      setSuccess(`Team member ${user.name} ${user.status === 'active' ? 'deactivated' : 'activated'} successfully!`);

      // Reload users
      loadUsers();
      loadStats();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle team member status. Please try again.');
    }
  };

  /**
   * Get badge variant for tag
   */
  const getTagBadgeVariant = (tag: string) => {
    switch (tag) {
      case 'admin': return 'danger';
      case 'developer': return 'primary';
      case 'designer': return 'info';
      case 'compliance': return 'warning';
      case 'tester': return 'success';
      default: return 'secondary';
    }
  };

  /**
   * Get badge variant for status
   */
  const getStatusBadgeVariant = (status: string) => {
    return status === 'active' ? 'success' : 'secondary';
  };

  return (
    <Container fluid className="admin-settings">
      <Row className="mb-4">
        <Col>
          <div className="settings-header">
            <div>
              <h2>
                <i className="bi bi-gear me-2"></i>
                Admin Settings
              </h2>
              <p className="text-muted">
                Manage team members{userSession.isAdmin ? '' : ` for ${userSession.adminEmail || 'your admin'}`}
              </p>
            </div>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <i className="bi bi-person-plus me-2"></i>
              Create Team Member
            </Button>
          </div>
        </Col>
      </Row>

      {/* Success/Error Messages */}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <div className="stat-icon total">
                  <i className="bi bi-people-fill"></i>
                </div>
                <h3>{stats.total}</h3>
                <p className="text-muted">Total Users</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <div className="stat-icon active">
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <h3>{stats.active}</h3>
                <p className="text-muted">Active Users</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <div className="stat-icon inactive">
                  <i className="bi bi-x-circle-fill"></i>
                </div>
                <h3>{stats.inactive}</h3>
                <p className="text-muted">Inactive Users</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body>
                <div className="stat-icon admin">
                  <i className="bi bi-shield-fill"></i>
                </div>
                <h3>{stats.byTag?.admin || 0}</h3>
                <p className="text-muted">Administrators</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Users Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Team Members</h5>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-people display-1 text-muted"></i>
                  <h4 className="mt-3">No team members yet</h4>
                  <p className="text-muted">Create your first team member to get started</p>
                  <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi bi-person-plus me-2"></i>
                    Create Team Member
                  </Button>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Tag</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <strong>{user.name}</strong>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <Badge bg={getTagBadgeVariant(user.tag)}>
                            {user.tag}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={getStatusBadgeVariant(user.status)}>
                            {user.status}
                          </Badge>
                        </td>
                        <td>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          {user.lastLogin
                            ? new Date(user.lastLogin).toLocaleString()
                            : 'Never'}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditUser(user)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant={user.status === 'active' ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            className="me-2"
                            onClick={() => handleToggleStatus(user)}
                            title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            <i className={`bi bi-${user.status === 'active' ? 'pause' : 'play'}-circle`}></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Team Member Modal */}
      <Modal show={showCreateModal} onHide={() => !isCreating && setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Team Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                placeholder="Enter full name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="Enter email address"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password *</Form.Label>
              <Form.Control
                type="password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
              <Form.Text className="text-muted">
                Minimum 6 characters recommended
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tag *</Form.Label>
              <Form.Select
                value={newUserTag}
                onChange={(e) => setNewUserTag(e.target.value as any)}
              >
                <option value="developer">Developer</option>
                <option value="designer">Designer</option>
                <option value="compliance">Compliance</option>
                <option value="tester">Tester</option>
                <option value="admin">Admin</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Note: All team members have full access to projects
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateUser} disabled={isCreating}>
            {isCreating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-person-plus me-2"></i>
                Create Team Member
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Team Member Modal */}
      {editingUser && (
        <Modal show={showEditModal} onHide={() => !isSaving && setShowEditModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Team Member</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={editingUser.email}
                  disabled
                  readOnly
                />
                <Form.Text className="text-muted">
                  Email cannot be changed
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tag</Form.Label>
                <Form.Select
                  value={editingUser.tag}
                  onChange={(e) => setEditingUser({ ...editingUser, tag: e.target.value as any })}
                >
                  <option value="developer">Developer</option>
                  <option value="designer">Designer</option>
                  <option value="compliance">Compliance</option>
                  <option value="tester">Tester</option>
                  <option value="admin">Admin</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Note: Tags are for identification only. All members have full access.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={editingUser.status}
                  onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value as any })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2"></i>
                  Save Changes
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default AdminSettings;

